import type { GrantablePermission } from '@palaccount/contracts';
import {
  cloneElement,
  isValidElement,
  useId,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useCan } from '@/lib/permissions';
import { useMe } from '@/lib/session';
import { cn } from '@/lib/utils';

/**
 * BR-PERM-17 / BR-PERM-18 — THE GREYED CONTROL.
 *
 * PART 16.7 row 2, in full, because both halves are requirements:
 *
 *   "The role has View but not the action → Show the screen and GREY OUT THE
 *    BUTTON. Resting the pointer on it must say why: *'Your role cannot delete
 *    bills.'* THE BUTTON MUST NEVER VANISH, or the person cannot tell whether
 *    the feature exists."
 *
 *   BR-PERM-17  A greyed-out button must always say why it is greyed out.
 *   BR-PERM-18  A button that a role can never use must be greyed out rather
 *               than hidden, so long as the person may open the screen at all.
 *
 * ── WHY THIS COMPONENT EXISTS ──────────────────────────────────────────────
 *
 * The build did the opposite, deliberately and everywhere: 164 `useCan` call
 * sites, about 226 of them shaped `can ? <Button/> : null`, with
 * `lib/permissions.ts` arguing the case as UX and never mentioning the two
 * rules it contradicted. The acceptance sweep found it and a test had pinned
 * it in place.
 *
 * The argument for hiding is real — a screen of dead buttons is a worse
 * screen — but it is an argument the specification has already heard and
 * answered, and the answer names the cost: a user who cannot see a control
 * cannot tell whether the feature exists, cannot describe what they are
 * missing, and cannot ask for it by name. That is the difference between a
 * product somebody learns and a product somebody guesses at.
 *
 * ── THE CONTRACT ───────────────────────────────────────────────────────────
 *
 * `<Gated permission="purchasing.delete" reasonKey="perm.cannot.deleteBills">`
 * wraps ONE interactive child. Held → the child, untouched. Not held → the
 * same child, inert, at 50% with the reason on hover, on focus, and to a
 * screen reader. Never `null`.
 *
 * WHAT IT IS NOT, said here because this is the file most likely to be
 * mistaken for a fence: it is not a check. BR-PERM-50 — "Hiding a control must
 * never be the only thing that prevents an action… the code the hidden button
 * called is still reachable." Disabling one is no different. Every action
 * behind one of these is refused by `PermissionGuard` on the server whatever
 * the browser drew, and the greying is there so the person understands the
 * screen, not so the screen is safe.
 *
 * ── WHEN NOT TO USE IT ─────────────────────────────────────────────────────
 *
 * 16.7 row 1 governs whole screens and MENU sections: those are ABSENT, not
 * greyed (BR-PERM-19). 16.7 row 3 governs a control blocked by a RULE or a
 * LIMIT rather than a permission — a closed period, an over-limit purchase
 * order: that button stays live and explains on click. This component is only
 * for row 2, the permission the role does not hold.
 *
 * ── GEN-56 SAYS THE OPPOSITE, AND 16.7 IS THE ONE THAT WINS ────────────────
 *
 * PART 4's button table carries GEN-56: "a button the user's role forbids must
 * be HIDDEN, not greyed out. A greyed button invites a support call." PART
 * 16.7 row 2 requires the reverse in the same words it forbids it — "the
 * button must never vanish, or the person cannot tell whether the feature
 * exists" — and gives its reason as a product decision rather than a style
 * note. PART 16 is the permission model's own chapter and is the later
 * writing, so it governs; GEN-56's stated worry (a support call) is answered
 * by the tooltip this component requires, which GEN-56 assumed would not be
 * there. Recorded rather than silently resolved: it is a real contradiction
 * between two parts of the specification, and a build that only read PART 4
 * would produce the opposite screen and believe it was right.
 *
 * NFR-36 lands here too: the child stays a real <button> or <a>. Disabling by
 * styling alone — a box that looks dead and still fires — is the defect
 * EC-19-49 and EC-19-58 both name (the substitute-material Apply was their
 * shared example), and `aria-disabled` plus the inert handler is what stops
 * it: a button that looks switched off IS switched off.
 *
 * GEN-164 RECORDED DIVERGENCE: PART 4 says a forbidden in-screen action
 * "must hide the button"; PART 16.7 (and GEN-56's greyed-is-a-lie flip
 * side) settled on Gated's grey-with-the-reason instead — a hidden button
 * teaches a user the feature does not exist, a greyed one with "ask your
 * administrator" teaches them who to ask. 16.7 is the permission chapter
 * and wins; GEN-181's real-control rule (= NFR-36) holds either way.
 */
export function Gated({
  permission,
  reasonKey,
  className,
  children,
}: {
  permission: GrantablePermission;
  /**
   * Layout classes for the wrapper span. The wrapper is a new box in the
   * layout, so a child that was `w-full` inside a grid needs its parent told —
   * otherwise greying a control would move it, and a control that moves when
   * you cannot use it is its own kind of lie.
   */
  className?: string;
  /**
   * An i18n key holding the VERB PHRASE only — "delete bills", "post bills".
   * The sentence around it is fixed in one place (`perm.roleCannot`) so the
   * wording cannot drift screen by screen, which is how 226 sites would have
   * ended up with 226 phrasings.
   */
  reasonKey: string;
  children: ReactElement<Record<string, unknown>>;
}) {
  const { t } = useTranslation();
  const allowed = useCan(permission);
  const reasonId = useId();

  if (allowed) return children;
  if (!isValidElement(children)) return children;

  return renderDenied({
    children,
    reason: t('perm.roleCannot', { what: t(reasonKey) }),
    reasonId,
    marker: permission,
    className,
  });
}

/**
 * BR-PERM-18 for a surface the Owner FLAG holds and no permission can reach.
 *
 * Ruling R18 (PART 16.4.5): "Owner" is a flag on exactly one account, not a
 * role — "there is no Owner card in the rail, no Owner grid to draw, and no
 * Owner entry in any picker anywhere." A handful of surfaces are governed by
 * that flag alone: granting or revoking the Administrator role, the six
 * owner-only settings, billing, transferring the flag itself.
 *
 * Where such a surface IS greyed, the SENTENCE has to be a different one, and
 * that is why this is a separate component rather than a prop on `Gated`:
 * "Your role cannot grant the Administrator role" invites the person to go and
 * ask for a permission that cannot be granted to anybody, ever — an errand
 * with no end. "Only the account owner can grant the Administrator role" tells
 * them the truth and tells them who to ask.
 *
 * ── IT HAS NO CALL SITE TODAY, AND THAT IS THE FINDING ─────────────────────
 *
 * The permission-UI sweep converted every Owner-only surface it could find and
 * put all of them back, because PART 16's screen chapters rule against greying
 * each one BY NAME and give reasons:
 *
 *   - the Administrator option in the Users role picker — ADM-06 §11.2:
 *     "must therefore be ABSENT… NOT GREYED OUT: an admin has no legitimate
 *     use for seeing it" (BR-ADM-06-21);
 *   - Transfer ownership — §10b step 1: "Nobody else sees this item, on any
 *     row";
 *   - Change role on an administrator's row — BR-PERM-72;
 *   - the six owner-only settings — the API never sends those rows to anybody
 *     else, so there is nothing on the screen to grey.
 *
 * The component is kept, tested and documented rather than deleted, because
 * the next Owner-only surface that is NOT ruled absent needs this sentence and
 * must not invent a second one. `permission-gate.test.tsx` pins it.
 */
export function OwnerOnly({
  reasonKey,
  className,
  children,
}: {
  className?: string;
  /** A verb phrase, as `Gated`'s — the sentence is `perm.ownerOnly`. */
  reasonKey: string;
  children: ReactElement<Record<string, unknown>>;
}) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const reasonId = useId();

  // Optimistic while /api/me is in flight, exactly as `useCan` is: the server
  // enforces regardless, and a flash of a live control beats greying work the
  // person may legitimately do.
  if (!me || me.user.isOwner) return children;
  if (!isValidElement(children)) return children;

  return renderDenied({
    children,
    reason: t('perm.ownerOnly', { what: t(reasonKey) }),
    reasonId,
    marker: 'owner',
    className,
  });
}

/**
 * The one place a refused control is DRAWN, shared by both gates so the two
 * sentences cannot drift into two different shapes of button.
 *
 * The reason rides on the WRAPPER for a pointer — the child is
 * pointer-events-none so it cannot be clicked, which would swallow its own
 * title on hover — and on a DESCRIPTION for a screen reader.
 *
 * A description rather than an `aria-label`, and the difference matters: a
 * label REPLACES the button's name, so "Delete" would become "Your role cannot
 * delete bills" and the control would no longer be findable by the word
 * printed on it (WCAG 2.5.3, and §21.17's "every control… named"). A
 * description is read AFTER the name, which is the order the person needs:
 * what it is, then why they cannot use it. BR-PERM-18's whole point is that
 * they learn the feature exists.
 */
function renderDenied({
  children,
  reason,
  reasonId,
  marker,
  className,
}: {
  children: ReactElement<Record<string, unknown>>;
  reason: string;
  reasonId: string;
  marker: string;
  className: string | undefined;
}) {
  return (
    <span className={cn('inline-flex', className)} title={reason} data-permission-denied={marker}>
      {cloneElement(children, {
        ...inertProps(children),
        'aria-disabled': true,
        'aria-describedby': reasonId,
        tabIndex: -1,
        className: cn(
          children.props['className'] as string | undefined,
          'pointer-events-none opacity-50',
        ),
      })}
      <span id={reasonId} className="sr-only">
        {reason}
      </span>
    </span>
  );
}

/**
 * `disabled` — except for a control that is really a LINK.
 *
 * Half the product's actions are `<Button asChild><Link to="…">New</Link></Button>`:
 * a button to look at, an anchor in the DOM. `disabled` means nothing on an
 * anchor — no browser greys it, no assistive technology announces it, and
 * `href` keeps working — so a link cloned with `disabled` would LOOK refused
 * and still navigate on Enter.
 *
 * So a denied link is turned into a real `<button disabled>` carrying the same
 * label and the same styling: `asChild` off, and the anchor's own children
 * lifted in its place. The person sees the identical control, greyed, named,
 * genuinely inert, and the destination is simply not reachable from it. (The
 * ROUTE is still guarded — `RequirePermission` and the server both — because
 * an address bar is not a button.)
 */
function inertProps(children: ReactElement<Record<string, unknown>>): Record<string, unknown> {
  if (children.props['asChild'] !== true) return { disabled: true };
  const inner = children.props['children'];
  return {
    disabled: true,
    asChild: false,
    children: isValidElement<{ children?: ReactNode }>(inner) ? inner.props.children : inner,
  };
}

/**
 * The same rule for a control that is not a single element — a row of them, or
 * a fragment. Renders the children inert inside one greyed wrapper carrying
 * the reason.
 */
export function GatedGroup({
  permission,
  reasonKey,
  children,
}: {
  permission: GrantablePermission;
  reasonKey: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const allowed = useCan(permission);
  if (allowed) return children;

  const reason = t('perm.roleCannot', { what: t(reasonKey) });
  return (
    <span
      className="inline-flex gap-2 opacity-50 [&_*]:pointer-events-none"
      title={reason}
      aria-label={reason}
      data-permission-denied={permission}
    >
      {children}
    </span>
  );
}
